import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Brain, Send, Sparkles, User, Loader2, Mic, MicOff, StopCircle } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIAssistantPage = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const scrollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    // Add welcome message
    setMessages([{
      role: 'assistant',
      content: `Hello ${user?.name?.split(' ')[0]}! I'm your AI HR Assistant powered by Gemini. I can help you with:\n\n- **Leave policies** and balance inquiries\n- **Payroll** and salary questions\n- **Skills development** recommendations\n- **Company policies** and HR processes\n- **OKR guidance** and goal setting\n- **Benefits** and ESOP information\n\nYou can type your question or click the microphone button to speak!\n\nHow can I assist you today?`
    }]);
  }, [user]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Process the recording
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success('Recording started - speak your question');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await axios.post(`${API_URL}/api/ai/transcribe`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.text) {
        setInput(response.data.text);
        toast.success('Voice transcribed! Click send or edit your message.');
      } else {
        toast.error('Could not understand the audio. Please try again.');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      const detail = error?.response?.data?.detail;
      toast.error(detail ? `Voice transcription failed: ${detail}` : 'Failed to transcribe audio. Please try again or type your question.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/ai/query`, {
        query: userMessage
      }, { withCredentials: true });

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response 
      }]);
    } catch (error) {
      console.error('AI query error:', error);
      toast.error('Failed to get AI response');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I encountered an error processing your request. Please try again." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const suggestedQuestions = [
    "What is the leave policy?",
    "How do I update my skills?",
    "Tell me about ESOP benefits",
    "How to set OKRs?",
    "What certifications should I pursue?"
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ai-assistant-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Brain className="w-7 h-7 text-[hsl(var(--ai-accent))]" />
          AI HR Assistant
        </h1>
        <p className="text-muted-foreground">Get instant answers to your HR queries powered by Gemini 3 Flash + Voice Input</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat Interface */}
        <Card className="lg:col-span-2 ai-panel">
          <CardContent className="p-0 flex flex-col h-[600px]">
            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {message.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-[hsl(var(--ai-accent))]/20 flex items-center justify-center shrink-0">
                        <Brain className="w-4 h-4 text-[hsl(var(--ai-accent))]" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      {message.role === 'assistant' ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <ReactMarkdown>{message.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <p className="text-sm">{message.content}</p>
                      )}
                    </div>
                    {message.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                ))}
                {isLoading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-[hsl(var(--ai-accent))]/20 flex items-center justify-center shrink-0">
                      <Brain className="w-4 h-4 text-[hsl(var(--ai-accent))]" />
                    </div>
                    <div className="bg-muted rounded-lg p-3">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  </div>
                )}
                <div ref={scrollRef} />
              </div>
            </ScrollArea>

            {/* Suggested Questions */}
            {messages.length <= 1 && (
              <div className="px-4 pb-2">
                <p className="text-xs text-muted-foreground mb-2">Suggested questions:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(q)}
                      className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 transition-colors"
                      data-testid={`suggested-q-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input with Voice */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-border">
              <div className="flex gap-2">
                {/* Voice Recording Button */}
                <Button
                  type="button"
                  variant={isRecording ? "destructive" : "outline"}
                  size="icon"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isLoading || isTranscribing}
                  className={`shrink-0 ${isRecording ? 'animate-pulse' : ''}`}
                  data-testid="voice-btn"
                >
                  {isTranscribing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isRecording ? (
                    <StopCircle className="w-4 h-4" />
                  ) : (
                    <Mic className="w-4 h-4" />
                  )}
                </Button>
                
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={isRecording ? "Recording... Click stop when done" : isTranscribing ? "Transcribing..." : "Ask me anything about HR..."}
                  disabled={isLoading || isRecording}
                  className="flex-1"
                  data-testid="ai-input"
                />
                <Button type="submit" disabled={isLoading || !input.trim() || isRecording} data-testid="ai-send-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              {isRecording && (
                <p className="text-xs text-[hsl(var(--ai-accent))] mt-2 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
                  Recording in progress... Speak your question clearly
                </p>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Quick Info & Insights */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[hsl(var(--ai-accent))]" />
                AI Capabilities
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {[
                { title: 'Policy Expert', desc: 'Ask about leave, attendance, or HR policies' },
                { title: 'Career Guide', desc: 'Get skill recommendations and growth advice' },
                { title: 'Benefits Advisor', desc: 'Understand ESOP, insurance, and perks' },
                { title: 'Performance Coach', desc: 'OKR setting and progress tracking help' },
                { title: 'Voice Input', desc: 'Speak your questions hands-free' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-2 rounded-md hover:bg-muted transition-colors">
                  <div className="w-2 h-2 rounded-full bg-[hsl(var(--ai-accent))] mt-1.5" />
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mic className="w-5 h-5 text-primary" />
                Voice Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                <li>Click the microphone button</li>
                <li>Speak your question clearly</li>
                <li>Click stop when finished</li>
                <li>Review & edit if needed</li>
                <li>Click send to get your answer</li>
              </ol>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Facts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">AI Model</span>
                <span className="font-mono text-xs">Gemini 3 Flash</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Voice</span>
                <span className="font-mono text-xs">Whisper STT</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Context</span>
                <span className="font-mono text-xs">HR Knowledge</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Privacy</span>
                <span className="badge-success text-xs px-2 py-0.5 rounded-full">Secure</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantPage;
